// undo-redo.service.ts
import { Injectable } from '@angular/core';
import { CanvasWorkflow } from './canvas-workflow.interface';

export interface WorkflowState {
  workflow: CanvasWorkflow;
  timestamp: number;
  action: string; // Description de l'action
}

@Injectable({
  providedIn: 'root'
})
export class UndoRedoService {
  private history: WorkflowState[] = [];
  private currentIndex: number = -1;
  private maxHistorySize: number = 50;

  /**
   * Sauvegarde un état du workflow
   */
  saveState(workflow: CanvasWorkflow, actionDescription: string): void {
    // Supprime tous les états après l'index actuel (si on était revenus en arrière)
    this.history = this.history.slice(0, this.currentIndex + 1);
    
    // Clone profond du workflow pour éviter les références
    const clonedWorkflow = this.deepCloneWorkflow(workflow);
    
    const state: WorkflowState = {
      workflow: clonedWorkflow,
      timestamp: Date.now(),
      action: actionDescription
    };

    this.history.push(state);
    this.currentIndex = this.history.length - 1;

    // Limite la taille de l'historique
    if (this.history.length > this.maxHistorySize) {
      this.history.shift();
      this.currentIndex--;
    }
  }

  /**
   * Revient à l'état précédent
   */
  undo(): CanvasWorkflow | null {
    if (!this.canUndo()) return null;
    
    this.currentIndex--;
    return this.deepCloneWorkflow(this.history[this.currentIndex].workflow);
  }

  /**
   * Avance à l'état suivant
   */
  redo(): CanvasWorkflow | null {
    if (!this.canRedo()) return null;
    
    this.currentIndex++;
    return this.deepCloneWorkflow(this.history[this.currentIndex].workflow);
  }

  /**
   * Vérifie si on peut faire un undo
   */
  canUndo(): boolean {
    return this.currentIndex > 0;
  }

  /**
   * Vérifie si on peut faire un redo
   */
  canRedo(): boolean {
    return this.currentIndex < this.history.length - 1;
  }

  /**
   * Récupère l'action qui sera annulée
   */
  getUndoAction(): string | null {
    if (!this.canUndo()) return null;
    return this.history[this.currentIndex].action;
  }

  /**
   * Récupère l'action qui sera refaite
   */
  getRedoAction(): string | null {
    if (!this.canRedo()) return null;
    return this.history[this.currentIndex + 1].action;
  }

  /**
   * Réinitialise l'historique
   */
  clear(): void {
    this.history = [];
    this.currentIndex = -1;
  }

  /**
   * Clone en profondeur le workflow
   */
  private deepCloneWorkflow(workflow: CanvasWorkflow): CanvasWorkflow {
    return(structuredClone(workflow)); 
  }

  /**
   * Récupère l'historique pour debug/affichage
   */
  getHistory(): WorkflowState[] {
    return [...this.history];
  }

  /**
   * Récupère l'index actuel
   */
  getCurrentIndex(): number {
    return this.currentIndex;
  }
}